import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { TOKEN_KEY } from "./backend";
import { defaultUserInfo } from "./defaultUserInfo";

const loadUserInfo = async () => {
    return defaultUserInfo;
};

const loginFunction = async () => {
    await wait(1500);
    localStorage.setItem(TOKEN_KEY, "123456");
    return defaultUserInfo;
};

const signupFunction = async () => {
    await wait(500);
    localStorage.setItem(TOKEN_KEY, "123456");
    return { ...defaultUserInfo, initialized: false };
};

const UserContext = createContext({});
const SecurityContext = createContext({
    login: () => undefined,
    signup: () => undefined,
    logout: () => undefined,
});

export const useUserInfo = () => {
    return useContext(UserContext);
};

export const useSecurity = () => {
    return useContext(SecurityContext);
};

export const Security = ({ children, SplashScreen }) => {
    const [state, setState] = useState({ initializing: true });
    const [userInfoLoading, setUserInfoLoading] = useState(false);

    /*
    const updateUserInfo = useCallback(
        async (userInfo) => {
            setLoading(true);
            try {
                await security.updateUserInfo(userInfo);
            } finally {
                setLoading(false);
            }
        },
        [security, setLoading]
    );
        */

    const value = useMemo(() => {
        const userInfo = state.userInfo;
        if (userInfo) {
            return {
                userInfo,
                setUserInfo: (userInfo) => {
                    setState({ initializing: false, userInfo });
                },
                updateUserInfo: async (userInfo) => {
                    console.log("context.updateUserInfo", userInfo);
                    setUserInfoLoading(true);
                    await wait(400);
                    setState({ userInfo, initializing: false });
                    setUserInfoLoading(false);
                },
                userInfoLoading,
                hasRight: (entitlement, reference, id) => {
                    if (!!userInfo.owner) {
                        return true;
                    }

                    let ent = userInfo.entitlements && userInfo.entitlements[entitlement];
                    if (!ent) {
                        return false;
                    }

                    if (!reference) {
                        return true;
                    }

                    let ref = ent[reference];
                    if (!ref) {
                        return false;
                    }

                    if (ref.all || (Array.isArray(ref.values) && ref.values.includes(id))) {
                        return true;
                    }

                    return false;
                },
            };
        }

        return {
            hasRight: () => false,
            setUserInfo: (userInfo) => {
                setState({ initializing: false, userInfo });
            },
        };
    }, [state.userInfo, setState, setUserInfoLoading, userInfoLoading]);

    const securityFunctions = useMemo(
        () => ({
            login: async (username, password, onSuccess) => {
                let userInfo = await loginFunction(username, password);
                if (onSuccess) {
                    await onSuccess(userInfo);
                }
                setState({ userInfo, initializing: false });
            },
            logout: (onSuccess) => {
                localStorage.removeItem(TOKEN_KEY);
                setState({});
                onSuccess && onSuccess();
            },
            signup: async (username, password, onSuccess) => {
                let userInfo = await signupFunction(username, password);
                if (onSuccess) {
                    await onSuccess(userInfo);
                }
                setState({ userInfo, initializing: false });
            },
            updateUserInfo: async (userInfo) => {
                await wait(400);
                setState({ userInfo, initializing: false });
            },
        }),
        [setState]
    );

    useEffect(() => {
        const checkUserInfo = async () => {
            //Check login token existence. If it is there, then try to load user info from server
            let token = localStorage.getItem(TOKEN_KEY);
            if (!!token) {
                setState({ initializing: true });
                let userInfo = await loadUserInfo();
                setState({ initializing: false, userInfo });
                return;
            }
            setState({ initializing: false });
        };
        checkUserInfo();
    }, []);

    if (state.initializing) {
        return (
            SplashScreen || (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                    <CircularProgress />
                </Box>
            )
        );
    }

    return (
        <SecurityContext.Provider value={securityFunctions}>
            <UserContext.Provider value={value}>{children}</UserContext.Provider>
        </SecurityContext.Provider>
    );
};

const wait = async (ms) => {
    return new Promise((resolve) => {
        setTimeout(() => resolve(1), ms);
    });
};
